import PropTypes from "prop-types";
import React from "react";
import { useSelector } from "react-redux";
import {
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  ReferenceArea,
  ResponsiveContainer,
  Scatter,
  XAxis,
  YAxis,
} from "recharts";

import {
  getCurveInfoGroup,
  getLegendInfo,
  graphType,
} from "../../../model/Common";
import {
  chartSetting,
  chartType,
  dataGraphLabel,
  rangeType,
} from "../../../model/DataGraph";
import { selector as dataGraphStoreSelector } from "../../../store/DataGraphStore";
import { selector as referenceLineStoreSelector } from "../../../store/ReferenceLineStore";

import Line from "./Line";

// 凡例作成
const createLegend = (chart, keys, referenceLine) => {
  const result = [];
  keys.forEach((key) => {
    // グラフ線図データが対象が存在するか確認
    const target = referenceLine.find((line) => line.type === key);
    if (target) {
      const setting = getLegendInfo(key);
      if (setting) {
        result.push({
          value: target.name,
          type: setting.icon,
          color: setting.color,
          payload: setting.option,
        });
      }
    }
  });

  if (chart === chartType[0].value) {
    // Shot数分布の固定凡例を追加
    result.push({
      value: dataGraphLabel.lineShot1,
      type: "square",
      color: "#e7f4ff",
    });
    result.push({
      value: dataGraphLabel.lineShot2,
      type: "square",
      color: "#fff643",
    });
    result.push({
      value: dataGraphLabel.lineShot3,
      type: "square",
      color: "#ffc74b",
    });
    result.push({
      value: dataGraphLabel.lineShot4,
      type: "square",
      color: "#a8a8aa",
    });
    result.push({
      value: dataGraphLabel.lineShot5,
      type: "square",
      color: "#ff5353",
    });
    result.push({
      value: dataGraphLabel.lineShot6,
      type: "square",
      color: "#667584",
    });
    result.push({
      value: dataGraphLabel.lineShot7,
      type: "square",
      color: "#532476",
    });
  }
  return result;
};

/* 凡例フォント設定 */
const renderColorfulLegendText = (value) => (
  <span style={{ color: "black", fontSize: 14, verticalAlign: "middle" }}>
    {value}
  </span>
);

/* 表示領域、メモリ情報作成 */
const createConfig = (chart, keys, data, referenceLine, direction) => {
  const points = [];
  const step = direction === 0 ? chartSetting.x.step : chartSetting.y.step;
  let max = direction === 0 ? chartSetting.x.max : chartSetting.y.max;
  let min = direction === 0 ? chartSetting.x.min : chartSetting.y.min;

  if (referenceLine.length > 0) {
    // 対象の線種データから、座標データを抽出(direction: 0 = 横軸 / 1 = 縦軸)
    keys.forEach((key) => {
      // グラフ線図データが対象が存在するか確認
      const target = referenceLine.find((line) => line.type === key);
      if (target) {
        target.value.forEach((point) => {
          points.push(point[direction]);
        });
      }
    });

    // 座標データ有り = 採用する線種データが存在する場合は最大/最小値を計算
    if (points.length > 0) {
      // 縦軸の場合、グラフデータの最大、最小も考慮する
      if (direction === 1) {
        data.forEach((item) => {
          if (chart === chartType[1].value) {
            // 製品単位
            points.push(item.totalLoad);
          } else if (chart === chartType[0].value) {
            // Shot数単位
            points.push(item.y2);
          }
        });
      }

      // 最大/最小値算出
      max = Math.ceil(Math.max(...points) / step) * step;
      min = Math.floor(Math.min(...points) / step) * step;

      if (direction === 0) {
        // X軸(横軸)の場合、左右に1マス分の余白を考慮
        max += step;
        min -= step;
      }
    }
  }

  // メモリ作成
  const label = [];

  // 最小値登録
  label.push(min);

  // 中間値登録
  let value = min;
  do {
    value += step;
    label.push(value);
  } while (value < max);

  return { max, min, label };
};

/* 分布データ 表示色取得 */
const getDataColor = (count) => {
  if (count <= 100) return "#e7f4ff";
  if (count <= 1000) return "#fff643";
  if (count <= 5000) return "#ffc74b";
  if (count <= 10000) return "#a8a8aa";
  if (count <= 20000) return "#ff5353";
  if (count <= 30000) return "#667584";
  return "#532476";
};

/* X軸メモリの表示調整(メモリを縦書きに調整) */
const CustomizedXAxisTick = ({ x, y, payload }) => {
  if (x && y && payload && typeof payload.value === "number") {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <text dy={5} textAnchor="end" transform="rotate(-90)">
          {payload.value}
        </text>
      </g>
    );
  }
  return null;
};

/**
 * 荷重 - 製品単位グラフコンポーネント.
 *
 * @param {string} chart グラフ種別(製品単位/Shot数単位)
 * @param {string} range グラフ種別(当日/当月)
 * @param {string} type グラフ種別(左右方向/前後方向)
 * @return {object} コンポーネント
 */
const Chart = React.forwardRef((props, ref) => {
  // 引数
  const {
    // グラフ種別(製品単位/Shot数単位)
    chart,
    // グラフ種別(当日/当月)
    range,
    // グラフ種別(左右方向/前後方向)
    type,
  } = props;

  // Store情報
  const {
    // 製品単位データ一覧
    productDatas,
    // Shot数単位エリアデータ一覧
    shotAreaDatas,
  } = useSelector(dataGraphStoreSelector);

  const {
    // グラフ線図情報(DB取得)
    referenceLines,
  } = useSelector(referenceLineStoreSelector);

  // 引数の組み合わせから表示データを指定する
  let data = [];
  if (chart === chartType[1].value) {
    if (range === rangeType[1].value) {
      if (type === graphType.lr) {
        // 製品単位 > 当日 > 左右方向
        data = productDatas.daily.x;
      } else if (type === graphType.fb) {
        // 製品単位 > 当日 > 前後方向
        data = productDatas.daily.y;
      }
    } else if (range === rangeType[0].value) {
      if (type === graphType.lr) {
        // 製品単位 > 当月 > 左右方向
        data = productDatas.month.x;
      } else if (type === graphType.fb) {
        // 製品単位 > 当月 > 前後方向
        data = productDatas.month.y;
      }
    }
  } else if (chart === chartType[0].value) {
    if (range === rangeType[1].value) {
      if (type === graphType.lr) {
        // Shot数単位 > 当日 > 左右方向
        data = shotAreaDatas.daily.x;
      } else if (type === graphType.fb) {
        // Shot数単位 > 当日 > 前後方向
        data = shotAreaDatas.daily.y;
      }
    } else if (range === rangeType[0].value) {
      if (type === graphType.lr) {
        // Shot数単位 > 当月 > 左右方向
        data = shotAreaDatas.month.x;
      } else if (type === graphType.fb) {
        // Shot数単位 > 当月 > 前後方向
        data = shotAreaDatas.month.y;
      }
    }
  }

  // 表示線種
  const lineInfo = getCurveInfoGroup(type);
  const loadType = lineInfo.load.key;
  const curve1Type = lineInfo.curve1.key;
  const curve2Type = lineInfo.curve2.key;
  const curve3Type = lineInfo.curve3.key;
  const curve4Type = lineInfo.curve4.key;
  const curve5Type = lineInfo.curve5.key;
  const line1Type = lineInfo.line1.key;
  const line2Type = lineInfo.line2.key;
  const line3Type = lineInfo.line3.key;
  const line4Type = lineInfo.line4.key;
  const keys = [
    loadType,
    curve1Type,
    curve2Type,
    curve3Type,
    curve4Type,
    curve5Type,
    line1Type,
    line2Type,
    line3Type,
    line4Type,
  ];
  const legendKeys = [
    loadType,
    curve1Type,
    curve2Type,
    curve3Type,
    curve4Type,
    curve5Type,
  ];

  // 絶対上下限値の範囲外データを排除したグラフ線図データを作成
  const recreateReferenceLines = [];
  referenceLines.forEach((line) => {
    // 絶対上限値を上回る、または、絶対下限値を下回るグラフ線図データを排除
    const filteredValues = line.value.filter(
      (point) =>
        point[1] <= chartSetting.y.max && point[1] >= chartSetting.y.min
    );

    if (filteredValues.length > 0) {
      recreateReferenceLines.push({ ...line, value: filteredValues });
    }
  });

  // 1) X軸(偏心量)の表示範囲情報作成
  const configX = createConfig(chart, keys, data, recreateReferenceLines, 0);

  // 2) グラフデータからX軸(偏心量)の表示範囲を超過するデータを排除
  const item = [];
  data.forEach((value) => {
    let target;
    if (chart === chartType[1].value) {
      // 製品単位
      target = value.eccentricity;
    } else if (chart === chartType[0].value) {
      // Shot数単位
      target = value.x2;
    }

    if (target && configX.min <= target && target <= configX.max) {
      item.push(value);
    }
  });

  // 3) グラフデータからY軸(合計荷重)の絶対上下限値を超過するデータを排除
  const itemLimited = [];
  item.forEach((value) => {
    let target;
    if (chart === chartType[1].value) {
      // 製品単位
      target = value.totalLoad;
    } else if (chart === chartType[0].value) {
      // Shot数単位
      target = value.y1;
    }
    if (target <= chartSetting.y.max && target >= chartSetting.y.min) {
      itemLimited.push(value);
    }
  });

  // 4) X軸(偏心量)の超過データとY軸(合計荷重)の超過データを排除後のグラフデータを元にY軸(合計荷重)の表示範囲情報作成
  const configY = createConfig(
    chart,
    keys,
    itemLimited,
    recreateReferenceLines,
    1
  );

  return (
    <ResponsiveContainer width="100%" aspect={1}>
      <ComposedChart
        ref={ref}
        data={itemLimited}
        margin={{
          top: 15,
          right: 15,
          bottom: 60,
          left: 15,
        }}
      >
        <CartesianGrid />
        <XAxis
          dataKey="eccentricity"
          domain={[configX.min, configX.max]}
          interval={0}
          tick={<CustomizedXAxisTick />}
          ticks={configX.label}
          type="number"
          label={{
            value: dataGraphLabel.axisX(type),
            position: "insideBottom",
            dy: 45,
          }}
        />
        <YAxis
          domain={[configY.min, configY.max]}
          interval={0}
          ticks={configY.label}
          type="number"
          label={{
            value: dataGraphLabel.axisY,
            angle: -90,
            position: "insideLeft",
            dx: -15,
          }}
        />
        <Legend
          payload={createLegend(chart, legendKeys, recreateReferenceLines)}
          formatter={renderColorfulLegendText}
          layout="vertical"
          verticalAlign="middle"
          align="center"
          iconSize={16}
          width={238}
          wrapperStyle={{ top: 15, left: 80 }}
        />
        {chart === chartType[1].value ? (
          <Scatter
            name="product"
            dataKey="totalLoad"
            fill="blue"
            isAnimationActive={false}
            shape="square"
          >
            <LabelList dataKey="productId" position="top" />
          </Scatter>
        ) : (
          <>
            {data.map((point) => (
              <ReferenceArea
                key={`${type}_Shot_Area_${point.x1}_${point.y1}`}
                x1={point.x1}
                y1={point.y1}
                x2={point.x2}
                y2={point.y2}
                stroke="black"
                strokeWidth={0.1}
                fill={getDataColor(point.dataCount)}
                fillOpacity={1}
              />
            ))}
          </>
        )}
        {Line(line1Type)}
        {Line(line2Type)}
        {Line(line3Type)}
        {Line(line4Type)}
        {Line(curve1Type)}
        {Line(curve2Type)}
        {Line(curve3Type)}
        {Line(curve4Type)}
        {Line(curve5Type)}
        {Line(loadType)}
      </ComposedChart>
    </ResponsiveContainer>
  );
});

CustomizedXAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({ value: PropTypes.number }),
};

CustomizedXAxisTick.defaultProps = {
  x: null,
  y: null,
  payload: null,
};

Chart.propTypes = {
  chart: PropTypes.string.isRequired,
  range: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

export default Chart;
